import type { DesignerAllTexts } from './texts.js';

/**
 * The designer's keyboard, as data.
 *
 * One table drives three things that used to drift apart: what the canvas
 * does when a key goes down, what the "Prečice na tastaturi" dialog lists,
 * and what the tests check. A shortcut that is not in this table does not
 * exist, and a row in the dialog that nothing implements cannot happen.
 *
 * It is deliberately free of the DOM: `matchShortcut` takes the four fields of
 * a KeyboardEvent it cares about, so the whole table can be exercised in the
 * widget's DOM-less test run.
 */

export type ShortcutId =
  | 'selectAll'
  | 'nudge' | 'nudgeSmall' | 'nudgeBig'
  | 'delete' | 'addText'
  | 'cut' | 'copy' | 'paste' | 'duplicate'
  | 'scaleUp' | 'scaleUpFine' | 'scaleDown' | 'scaleDownFine'
  | 'zoomIn' | 'zoomOut' | 'zoomFit'
  | 'undo' | 'redo'
  | 'group' | 'ungroup'
  | 'escape';

/** The four fields of a KeyboardEvent the table reads. */
export interface KeyStroke {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

export interface ShortcutRow {
  id: ShortcutId;
  /** The text key that names it in the dialog. */
  label: keyof DesignerAllTexts;
  /** What the dialog prints, already in the shopper's notation. */
  keys: string;
}

const ARROWS: ReadonlySet<string> = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

/** Command on a Mac is Ctrl everywhere else; the table never tells them apart. */
function meta(stroke: KeyStroke): boolean {
  return Boolean(stroke.ctrlKey || stroke.metaKey);
}

function isPlus(key: string): boolean {
  return key === '+' || key === '=' || key === 'Add';
}

function isMinus(key: string): boolean {
  return key === '-' || key === '_' || key === 'Subtract';
}

/**
 * Which shortcut a keystroke is, or undefined for a key the designer leaves
 * to the browser. Order matters where one chord is another plus a modifier:
 * Ctrl+Alt+− is tested before Ctrl+−, and Shift+Ctrl+Z before Ctrl+Z.
 */
export function matchShortcut(stroke: KeyStroke): ShortcutId | undefined {
  const key = stroke.key;
  const mod = meta(stroke);
  const shift = Boolean(stroke.shiftKey);
  const alt = Boolean(stroke.altKey);
  const lower = key.length === 1 ? key.toLowerCase() : key;

  if (key === 'Escape') return 'escape';
  if (key === 'Delete' || key === 'Backspace') return 'delete';

  if (ARROWS.has(key)) {
    // Alt+Arrow is the browser's history gesture on several platforms; the
    // designer leaves it alone rather than eating a shopper's "back".
    if (alt) return undefined;
    if (mod) return 'nudgeSmall';
    if (shift) return 'nudgeBig';
    return 'nudge';
  }

  if (mod && alt && isPlus(key)) return 'scaleUpFine';
  if (mod && alt && isMinus(key)) return 'scaleDownFine';
  if (mod && isPlus(key)) return 'zoomIn';
  if (mod && isMinus(key)) return 'zoomOut';
  if (mod && key === '0') return 'zoomFit';

  if (mod && lower === 'z') return shift ? 'redo' : 'undo';
  if (mod && lower === 'y') return 'redo';
  if (mod && lower === 'a') return 'selectAll';
  if (mod && lower === 'c') return 'copy';
  if (mod && lower === 'x') return 'cut';
  if (mod && lower === 'v') return 'paste';
  if (mod && lower === 'd') return 'duplicate';
  if (mod && lower === 'g') return shift ? 'ungroup' : 'group';

  if (mod || alt) return undefined;
  if (isPlus(key)) return 'scaleUp';
  if (isMinus(key)) return 'scaleDown';
  if (lower === 't') return 'addText';
  return undefined;
}

/** The arrow's direction, in unit steps the caller multiplies by millimetres. */
export function arrowDelta(key: string): { dx: number; dy: number } | undefined {
  switch (key) {
    case 'ArrowLeft': return { dx: -1, dy: 0 };
    case 'ArrowRight': return { dx: 1, dy: 0 };
    case 'ArrowUp': return { dx: 0, dy: -1 };
    case 'ArrowDown': return { dx: 0, dy: 1 };
    default: return undefined;
  }
}

/** How far an arrow moves: Ctrl is fine, Shift is coarse, bare is 1 mm. */
export function nudgeStepMm(stroke: KeyStroke): number {
  if (meta(stroke)) return 0.5;
  if (stroke.shiftKey) return 5;
  return 1;
}

/** Scale factors for the four size shortcuts. */
export const SCALE_FACTORS: Record<'scaleUp' | 'scaleDown' | 'scaleUpFine' | 'scaleDownFine', number> = {
  scaleUp: 1.1,
  scaleDown: 0.9,
  scaleUpFine: 1.02,
  scaleDownFine: 0.98,
};

/**
 * The dialog's rows, in Zazzle's order. Every id here is one `matchShortcut`
 * can return — the test asserts that both ways round.
 */
export const SHORTCUTS: ShortcutRow[] = [
  { id: 'selectAll', label: 'keySelectAll', keys: 'Ctrl + A' },
  { id: 'nudge', label: 'keyNudge', keys: '↑ ↓ ← →' },
  { id: 'nudgeSmall', label: 'keyNudgeSmall', keys: 'Ctrl + ↑ ↓ ← →' },
  { id: 'nudgeBig', label: 'keyNudgeBig', keys: 'Shift + ↑ ↓ ← →' },
  { id: 'delete', label: 'keyDelete', keys: 'Delete · Backspace' },
  { id: 'addText', label: 'keyAddText', keys: 'T' },
  { id: 'cut', label: 'keyCut', keys: 'Ctrl + X' },
  { id: 'copy', label: 'keyCopy', keys: 'Ctrl + C' },
  { id: 'paste', label: 'keyPaste', keys: 'Ctrl + V' },
  { id: 'duplicate', label: 'keyDuplicate', keys: 'Ctrl + D' },
  { id: 'scaleUp', label: 'keyScaleUp', keys: '+' },
  { id: 'scaleUpFine', label: 'keyScaleUpFine', keys: 'Ctrl + Alt + +' },
  { id: 'scaleDown', label: 'keyScaleDown', keys: '−' },
  { id: 'scaleDownFine', label: 'keyScaleDownFine', keys: 'Ctrl + Alt + −' },
  { id: 'zoomIn', label: 'keyZoomIn', keys: 'Ctrl + +' },
  { id: 'zoomOut', label: 'keyZoomOut', keys: 'Ctrl + −' },
  { id: 'zoomFit', label: 'keyZoomFit', keys: 'Ctrl + 0' },
  { id: 'undo', label: 'keyUndo', keys: 'Ctrl + Z' },
  { id: 'redo', label: 'keyRedo', keys: 'Shift + Ctrl + Z' },
  { id: 'group', label: 'keyGroup', keys: 'Ctrl + G' },
  { id: 'ungroup', label: 'keyUngroup', keys: 'Shift + Ctrl + G' },
  { id: 'escape', label: 'keyEscape', keys: 'Esc' },
];
